import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ClickOutHandler from 'react-onclickout'
import { withCurrency } from '../../../../lib/utils/money'

class PriceCombobox extends Component {
  static propTypes = {
    className: PropTypes.string,
    id: PropTypes.string.isRequired,
    isInvalid: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.number).isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.number
  }

  static defaultProps = {
    isInvalid: false,
    placeholder: ''
  }

  state = {
    dropdownPosition: { top: 0, left: 0, width: 0 },
    isFocused: false,
    inputValue: '',
    isOpen: false
  }

  componentDidMount() {
    this.updateInputValue(this.props.value)
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.updateDropdownPosition, true)
      window.addEventListener('resize', this.updateDropdownPosition)
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.updateDropdownPosition, true)
      window.removeEventListener('resize', this.updateDropdownPosition)
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value && !this.state.isFocused) {
      this.updateInputValue(this.props.value)
    }
  }

  updateInputValue = (value) => {
    const numValue = typeof value === 'number' ? value : Number(value)
    this.setState({
      inputValue: !isNaN(numValue) && (value || value === 0) ? withCurrency(numValue) : ''
    })
  }

  handleInputChange = (e) => {
    const rawValue = e.target.value
    this.setState({ inputValue: rawValue })

    const numericValue = rawValue.replace(/[^0-9]/g, '')
    this.props.onChange(numericValue === '' ? null : Number(numericValue))
  }

  handleInputFocus = (e) => {
    this.setState({ isFocused: true })
  }

  handleInputBlur = () => {
    this.setState({ isFocused: false })
    this.updateInputValue(this.props.value)
  }

  updateDropdownPosition = () => {
    if (!this.wrapperRef) return

    const rect = this.wrapperRef.getBoundingClientRect()
    this.setState({
      dropdownPosition: {
        left: rect.left,
        top: rect.bottom + 4,
        width: rect.width
      }
    })
  }

  toggleDropdown = () => {
    this.setState(prev => {
      const willOpen = !prev.isOpen
      if (willOpen) {
        this.updateDropdownPosition()
      }
      return { isOpen: willOpen }
    })
  }

  closeDropdown = () => {
    this.setState({ isOpen: false })
  }

  selectOption = (value) => {
    this.props.onChange(value)
    this.updateInputValue(value)
    this.closeDropdown()
  }

  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      this.closeDropdown()
    } else if (e.key === 'Escape') {
      this.closeDropdown()
    }
  }

  renderDropdown = () => {
    const { options } = this.props
    const { dropdownPosition, isOpen } = this.state

    if (!isOpen || typeof document === 'undefined') return null

    const dropdownStyle = {
      left: `${dropdownPosition.left}px`,
      position: 'fixed',
      top: `${dropdownPosition.top}px`,
      width: `${dropdownPosition.width}px`
    }

    return ReactDOM.createPortal(
      <div className='PriceCombobox-dropdown' style={dropdownStyle}>
        <div className='PriceCombobox-options'>
          {options.map(option => (
            <button
              key={option}
              type='button'
              className='PriceCombobox-option'
              onClick={() => this.selectOption(option)}
            >
              {withCurrency(option)}
            </button>
          ))}
        </div>
      </div>,
      document.body
    )
  }

  render() {
    const { className, id, isInvalid, name, placeholder } = this.props
    const { inputValue, isOpen } = this.state

    return (
      <ClickOutHandler onClickOut={this.closeDropdown}>
        <div
          ref={ref => { this.wrapperRef = ref }}
          className={classNames('PriceCombobox', className, { isInvalid, isOpen })}
        >
          <div className='PriceCombobox-inputWrapper'>
            <input
              ref={ref => { this.inputRef = ref }}
              type='text'
              inputMode='numeric'
              id={id}
              name={name}
              className='PriceCombobox-input'
              value={inputValue}
              onChange={this.handleInputChange}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputBlur}
              onKeyDown={this.handleKeyDown}
              placeholder={placeholder}
              aria-invalid={isInvalid || null}
            />
            <button
              type='button'
              className='PriceCombobox-trigger'
              onClick={this.toggleDropdown}
              aria-label='Toggle price options'
              tabIndex={-1}
            >
              <svg width='12' height='8' viewBox='0 0 12 8' fill='none'>
                <path d='M1 1.5L6 6.5L11 1.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round'/>
              </svg>
            </button>
          </div>

          {this.renderDropdown()}
        </div>
      </ClickOutHandler>
    )
  }
}

export default PriceCombobox
